import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../hooks/useLanguage";
import Icon from "../../../../common/components/Icon/Icon";

interface LanguageSwitcherProps {
  className?: string;
  variant?: "dropdown" | "inline";
}

const LanguageToCountryMap: Record<string, string> = {
  tr: "tr",
  en: "gb",
  de: "de",
  fr: "fr",
  es: "es",
  it: "it",
  ru: "ru",
};

const ShortNameMap: Record<string, string> = {
  tr: "TR",
  en: "EN",
  de: "DE",
  fr: "FR",
  es: "ES",
  it: "IT",
  ru: "RU",
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className,
  variant = "dropdown",
}) => {
  const { currentLanguage, supportedLanguages, switchLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside (Capture Phase & Touch Support)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [isOpen]);

  if (supportedLanguages.length < 2) {
    return null;
  }

  const handleLanguageSwitch = (lang: string) => {
    setIsOpen(false);
    if (lang === currentLanguage) return;
    switchLanguage(lang);
  };

  const currentCountryCode =
    LanguageToCountryMap[currentLanguage] || currentLanguage;
  const currentShortName =
    ShortNameMap[currentLanguage] || currentLanguage.toUpperCase();
  const currentFlagUrl = `https://flagcdn.com/${currentCountryCode}.svg`;

  // --- 1. YAN YANA YAZILI BUTON MODU (Geriye Uyumlu / Legacy) ---
  if (variant === "inline") {
    return (
      <div
        className={`language-switcher language-switcher--inline ${className || ""}`}
      >
        <div className="language-switcher__container">
          {supportedLanguages.map((lang) => {
            const countryCode = LanguageToCountryMap[lang] || lang;
            const shortName = ShortNameMap[lang] || lang.toUpperCase();
            const flagUrl = `https://flagcdn.com/${countryCode}.svg`;
            const isActive = lang === currentLanguage;

            return (
              <button
                key={lang}
                onClick={() => handleLanguageSwitch(lang)}
                className={`language-switcher__option ${isActive ? "is-active" : ""}`}
                aria-current={isActive ? "true" : "false"}
              >
                <img
                  src={flagUrl}
                  alt={shortName}
                  className="flag-img"
                  width="16"
                  height="11"
                  loading="lazy"
                />
                <span>{shortName}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // --- 2. DROPDOWN (AÇILIR MENÜ) MODU (Yeni Varsayılan) ---
  return (
    <div
      ref={dropdownRef}
      className={`language-switcher language-switcher--dropdown ${className || ""} ${isOpen ? "is-open" : ""}`}
    >
      <button
        type="button"
        className="language-switcher__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <img
          src={currentFlagUrl}
          alt={currentShortName}
          className="flag-img"
          width="16"
          height="11"
        />
        <span className="language-switcher__selected-name">
          {currentShortName}
        </span>
        <Icon iconName="chevron-down" className="language-switcher__chevron" />
      </button>

      {isOpen && (
        <ul className="language-switcher__menu" role="listbox">
          {supportedLanguages.map((lang) => {
            const countryCode = LanguageToCountryMap[lang] || lang;
            const shortName = ShortNameMap[lang] || lang.toUpperCase();
            const flagUrl = `https://flagcdn.com/${countryCode}.svg`;
            const isActive = lang === currentLanguage;

            return (
              <li key={lang} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleLanguageSwitch(lang)}
                  className={`language-switcher__menu-item ${isActive ? "is-active" : ""}`}
                >
                  <img
                    src={flagUrl}
                    alt={shortName}
                    className="flag-img"
                    width="16"
                    height="11"
                    loading="lazy"
                  />
                  <span>{shortName}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
